import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { ERRORE_GENERICO } from '../../../../utils/api/api';
import Intestazione from './Intestazione';
import useStili from './stili';

const NON_MODIFICATA = 'La Gestione Operativa non è stata modificata.';

const TITOLI = {
  verifica: 'Verifica non completata',
  scrittura: 'Import non completato',
};

const testoBase = (fase, nome) =>
  fase === 'verifica'
    ? `Non è stato possibile verificare «${nome}». ${NON_MODIFICATA}`
    : `Non è stato possibile importare «${nome}». ${NON_MODIFICATA}`;

// A write the answer never came back for: -1 is a request with no answer at
// all, 0 an answer that could not be read (a 200 whose body broke off among
// them), 502 and 504 a load balancer that may have handed it on. The backend
// may have written, so the screen does not claim it did not.
const STATUS_ESITO_IGNOTO = [-1, 0, 502, 504];

export const esitoIgnoto = (fase, status) =>
  fase === 'scrittura' && STATUS_ESITO_IGNOTO.includes(status);

/**
 * What the error screen says for a failure, and whether trying the same file
 * again can change anything. `dettaglio` is the request's own sentence, shown
 * only where it adds to the screen's: not the generic one, and not where the
 * screen already says the same.
 */
const descriviErrore = ({ fase, status, messaggio, nome }) => {
  const dettaglio =
    messaggio && messaggio !== ERRORE_GENERICO ? messaggio : null;

  if (status === 403) {
    return {
      titolo: 'Import non consentito',
      testo: `Non hai i permessi per importare una configurazione in questa Gestione Operativa. ${NON_MODIFICATA}`,
      dettaglio: null,
      riprovabile: false,
    };
  }
  if (status === 404) {
    return {
      titolo: 'Gestione Operativa non trovata',
      testo:
        'La Gestione Operativa di questo esercizio non è più disponibile: ricarica la pagina e controlla che esista ancora.',
      dettaglio: null,
      riprovabile: false,
    };
  }
  if (status === 409) {
    return {
      titolo: 'Pubblicazione in corso',
      testo:
        "È in corso una pubblicazione della Gestione Operativa: l'import non è stato eseguito. Riprova al termine della pubblicazione.",
      dettaglio: null,
      riprovabile: true,
    };
  }
  if (status === 413) {
    return {
      titolo: 'File troppo grande',
      testo: `«${nome}» supera la dimensione che il server accetta. ${NON_MODIFICATA}`,
      dettaglio: null,
      riprovabile: false,
    };
  }
  if (esitoIgnoto(fase, status)) {
    return {
      titolo: "Esito dell'import non confermato",
      testo: `La risposta all'import di «${nome}» non è arrivata: la Gestione Operativa potrebbe essere già stata aggiornata. Riprovando, lo stesso file la sostituisce di nuovo.`,
      dettaglio,
      riprovabile: true,
    };
  }
  return {
    titolo: TITOLI[fase],
    testo: testoBase(fase, nome),
    dettaglio,
    // A connection that dropped or a service being replaced comes back by
    // itself; anything else may too, and costs nothing to try.
    riprovabile: true,
  };
};

/**
 * The check or the write could not run: nothing is known to be wrong with the
 * file. The screen says why where the status tells it, and offers to try the
 * same file again only where that can change the outcome.
 */
const ErroreTecnico = ({
  fase,
  file,
  status,
  messaggio,
  onRiprova,
  onChiudi,
}) => {
  const stili = useStili();
  const { titolo, testo, dettaglio, riprovabile } = descriviErrore({
    fase,
    status,
    messaggio,
    nome: file.name,
  });

  return (
    <>
      <DialogContent className={stili.content}>
        <Intestazione tono="incerto" titolo={titolo} testo={testo} />
        {dettaglio && (
          <Typography variant="body2" className={stili.testo}>
            {dettaglio}
          </Typography>
        )}
      </DialogContent>
      <DialogActions disableSpacing className={stili.actions}>
        {riprovabile ? (
          <>
            <Button
              variant="outlined"
              className={stili.secondario}
              onClick={onChiudi}
            >
              Chiudi
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={stili.primario}
              onClick={onRiprova}
            >
              Riprova
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            className={stili.primario}
            onClick={onChiudi}
          >
            Chiudi
          </Button>
        )}
      </DialogActions>
    </>
  );
};

ErroreTecnico.propTypes = {
  fase: PropTypes.oneOf(['verifica', 'scrittura']).isRequired,
  file: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
  status: PropTypes.number,
  messaggio: PropTypes.string,
  onRiprova: PropTypes.func.isRequired,
  onChiudi: PropTypes.func.isRequired,
};

export default ErroreTecnico;
